import { useMemo, useState } from "react";
import { useLoaderData, useNavigate, useNavigation, useSubmit } from "react-router-dom";

import { useAuthStore } from "src/components/main/authStore";
import {
    buildWeekNumArray,
    getDateEndFromWeekNum,
    getDatefromWeekNum,
    getDateTime,
    MyDate,
    outsideEotDefaultEmptyData,
    simpleStatusColorCode,
    standbyStartDate,
    weekDays,
    weeknum,
} from "src/components/common/functions";

type EotDay = {
    availablity: string;
    yard: string;
};

type EotFormData = {
    form: EotDay[];
    status: string;
    unlock?: boolean;
};

type DivisionOption = {
    division: string;
};

type LoaderData = {
    divisions: DivisionOption[];
    form: EotFormData;
    week: string;
};

const title = "EmergencyOT";
const getStatusColor = (status: string) => (simpleStatusColorCode as Record<string, string>)[status] ?? "#6c757d";
const defaultForm = JSON.parse(outsideEotDefaultEmptyData) as EotFormData;

export default function EmergencyOT() {
    const { week, form: loadedForm, divisions } = useLoaderData() as LoaderData;
    const user = useAuthStore(state => state.user);
    const navigate = useNavigate();
    const navigation = useNavigation();
    const submit = useSubmit();
    const [form, setForm] = useState<EotFormData>(loadedForm);
    const [isEditingContent, setIsEditingContent] = useState(false);
    const isSubmitting = navigation.state === "submitting";
    const hireDateString = user?.hireDate ?? "";
    const quitDateString = user?.quitDate ?? "";

    const weeks = useMemo(
        () => buildWeekNumArray(standbyStartDate).filter((value) => (
            getDateTime(value, 3, 0, 0) > new Date(hireDateString.replace(/-/g, "/")) &&
            (quitDateString ? getDateTime(value, 3, 0, 0) <= new Date(quitDateString.replace(/-/g, "/")) : true)
        )).reverse(),
        [hireDateString, quitDateString]
    );

    const activeWeek = weeks.includes(week)
        ? week
        : (weeks.includes(weeknum(new MyDate().s())) ? weeknum(new MyDate().s()) : (weeks[0] || ""));

    const timeValid = (weekValue: string) => getDateTime(weekValue, 3, 11, 0) > new Date();
    const currentForm = form.form.length ? form : loadedForm;

    const updateDay = (dayIndex: number, updater: (day: EotDay) => EotDay) => {
        setForm((current) => ({
            ...current,
            form: current.form.map((day, index) => index === dayIndex ? updater(day) : day),
        }));
    };

    const submitForm = (nextForm: EotFormData) => {
        const formData = new FormData();
        formData.append("currentWeek", activeWeek);
        formData.append("data", JSON.stringify(nextForm));
        submit(formData, { method: "post" });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-end flex-wrap gap-3">
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "emergencyOTWeek">Week</label>
                    <select
                        id = "emergencyOTWeek"
                        className = "form-control w-auto"
                        value = {activeWeek}
                        onChange = {e => {
                            setIsEditingContent(false);
                            setForm(loadedForm);
                            navigate(`/${title}/${e.currentTarget.value}`);
                        }}
                    >
                        {weeks.map((value, index) => <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>)}
                    </select>
                </div>
            </nav>
            <div className = "p-3">
                <div className = "cssSeperateWhenWide align-items-center">
                    <div className = "h3 text-danger fw-bold mb-0">{getDateEndFromWeekNum(activeWeek)}</div>
                    <div className = "h4 mb-0 ms-2" style = {{ color: getStatusColor(currentForm.status) }}>{currentForm.status}</div>
                    {!timeValid(activeWeek) && !currentForm.unlock ? <span className = "ms-2 text-light fw-bold bg-secondary rounded text-nowrap px-2 py-1">past</span> : null}
                    {currentForm.form.every(day => day.availablity === "No") ? <span className = "ms-2 text-light fw-bold bg-secondary rounded text-nowrap px-2 py-1">All No</span> : null}
                    {currentForm.form.every(day => day.availablity === "Yes") ? <span className = "ms-2 text-light fw-bold bg-secondary rounded text-nowrap px-2 py-1">All Yes</span> : null}
                </div>
                <div className = "row mt-2">
                    {[...weekDays.slice(4), ...weekDays.slice(0, 4)].map((weekday, index) => (
                        <div className = "col-lg" key = {index}>
                            <div className = "h4 text-secondary">{weekday} ({getDatefromWeekNum(activeWeek, index + 4)})</div>
                            <div className = "mt-2">
                                {isEditingContent ? (
                                    <div>
                                        <label htmlFor = {`availablity-${index}`} className = "fw-bold">Availablity:</label>
                                        <select
                                            id = {`availablity-${index}`}
                                            required
                                            className = "form-control"
                                            value = {currentForm.form[index]?.availablity ?? ""}
                                            onChange = {e => {
                                                const value = e.currentTarget.value;
                                                updateDay(index, (day) => ({ ...day, availablity: value }));
                                            }}
                                        >
                                            <option value = "">Select</option>
                                            <option>Yes</option>
                                            <option>No</option>
                                        </select>
                                    </div>
                                ) : (
                                    <div>
                                        <label className = "fw-bold">Availablity:</label>
                                        <span className = "ms-2">{currentForm.form[index]?.availablity}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className = "mt-2">
                    <label htmlFor = "yard" className = "fw-bold">Yard:</label>
                    {isEditingContent ? (
                        <select
                            id = "yard"
                            className = "form-control w-auto"
                            required
                            value = {currentForm.form[0]?.yard ?? ""}
                            onChange = {e => {
                                const value = e.currentTarget.value;
                                updateDay(0, (day) => ({ ...day, yard: value }));
                            }}
                        >
                            <option value = "">Select</option>
                            {divisions.map((division, index) => <option key = {index}>{division.division}</option>)}
                        </select>
                    ) : <span className = "ms-2">{currentForm.form[0]?.yard}</span>}
                </div>
                {currentForm.status === "Submitted" || (!timeValid(activeWeek) && !currentForm.unlock) ? null : (
                    <div className = "text-right">
                        {isEditingContent ? (
                            <>
                                <button
                                    type = "button"
                                    className = "btn btn-primary mx-3"
                                    onClick = {() => {
                                        setForm((current) => ({
                                            ...current,
                                            form: current.form.map(day => ({ ...day, availablity: "No" })),
                                        }));
                                    }}
                                >
                                    All No
                                </button>
                                <button
                                    type = "button"
                                    className = "btn btn-primary mx-3"
                                    onClick = {() => {
                                        setForm((current) => ({
                                            ...current,
                                            form: current.form.map(day => ({ ...day, availablity: "Yes" })),
                                        }));
                                    }}
                                >
                                    All Yes
                                </button>
                                <button
                                    type = "button"
                                    className = "btn btn-primary mx-3"
                                    disabled = {isSubmitting}
                                    onClick = {() => {
                                        const nextForm = { ...currentForm, status: "Saved" };
                                        submitForm(nextForm);
                                        setIsEditingContent(false);
                                    }}
                                >
                                    {isSubmitting ? "Saving..." : "Save Content"}
                                </button>
                                <button
                                    type = "button"
                                    className = "btn btn-primary mx-3"
                                    disabled = {isSubmitting}
                                    onClick = {() => {
                                        if (!window.confirm("Are you sure to submit for the week?")) return;
                                        const nextForm = { ...currentForm, status: "Submitted" };
                                        submitForm(nextForm);
                                        setIsEditingContent(false);
                                    }}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit For the Week"}
                                </button>
                            </>
                        ) : (
                            <button
                                type = "button"
                                className = "btn btn-primary mx-3"
                                onClick = {e => {
                                    e.preventDefault();
                                    setForm(loadedForm.form.length ? loadedForm : defaultForm);
                                    setIsEditingContent(true);
                                }}
                            >
                                Edit Content
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
